/*
 * 52 ARCHIVES mobile header behaviour (below 750px).
 * Adapts Dawn's existing header-drawer, details-modal (search) and
 * cart-drawer components for the mobile layout. Tablet/desktop keep
 * Dawn's default behaviour.
 */
(function () {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const isMobile = () => mobileQuery.matches;

  /* Menu: the X in the drawer's top row closes it through Dawn's closeMenuDrawer. */
  document.addEventListener(
    'click',
    (event) => {
      const closeButton = event.target.closest('.menu-drawer__mobile-close');
      if (!closeButton) return;
      const drawer = closeButton.closest('header-drawer');
      const summary = drawer && drawer.querySelector('#Details-menu-drawer-container > summary');
      if (!summary) return;

      // Capture phase: keeps Dawn's generic submenu "close button" handler from also running.
      event.stopPropagation();
      drawer.closeMenuDrawer(event, summary);
      summary.setAttribute('aria-expanded', 'false');
    },
    true
  );

  /* Search: on mobile, open without focusing the input so the keyboard is not summoned. */
  customElements.whenDefined('details-modal').then(() => {
    const DetailsModal = customElements.get('details-modal');
    const originalOpen = DetailsModal.prototype.open;

    DetailsModal.prototype.open = function (event) {
      if (!isMobile() || !this.classList.contains('header__search')) return originalOpen.call(this, event);

      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      event.target.closest('details').setAttribute('open', true);
      document.body.addEventListener('click', this.onBodyClickEvent);
      document.body.classList.add('overflow-hidden');

      // Same focus trap as Dawn, focused on the dialog instead of the input.
      const container = this.detailsContainer.querySelector('[tabindex="-1"]');
      trapFocus(container, container);
    };
  });

  /* Bag: the cart drawer handles the bag on mobile (or everywhere if the cart type is "drawer"). */
  const cartLinkSelector = '#cart-icon-bubble';
  // Mobile on the cart page itself: the full cart is already on screen, so no drawer.
  const onMobileCartPage = () => isMobile() && window.location.pathname === (window.routes?.cart_url || '/cart');
  const drawerHandlesCart = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || onMobileCartPage()) return false;
    return isMobile() || cartDrawer.dataset.cartType === 'drawer';
  };

  ['click', 'keydown'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        if (!event.target.closest(cartLinkSelector) || drawerHandlesCart()) return;
        if (type === 'keydown' && event.code.toUpperCase() !== 'SPACE') return;
        // Tablet/desktop: stop cart-drawer.js from intercepting, so the link opens /cart as before.
        event.stopPropagation();
        // Mobile cart page: no drawer and no reload; back to the top of the cart instead.
        if (onMobileCartPage()) {
          event.preventDefault();
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      },
      true
    );
  });

  function syncCartLinkSemantics() {
    const cartLink = document.querySelector(cartLinkSelector);
    if (!cartLink) return;

    if (drawerHandlesCart()) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    } else {
      cartLink.removeAttribute('role');
      cartLink.removeAttribute('aria-haspopup');
    }
  }

  customElements.whenDefined('cart-drawer').then(syncCartLinkSemantics);
  mobileQuery.addEventListener('change', syncCartLinkSemantics);

  /*
   * Keep the drawer contents current when the cart changes outside it
   * (e.g. add to cart via the notification popup). Changes made inside the
   * open drawer are already re-rendered by Dawn.
   */
  function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || cartDrawer.classList.contains('active')) return;

    fetch(`${window.Shopify.routes.root}?sections=cart-drawer`)
      .then((response) => response.json())
      .then((sections) => {
        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const freshContents = html.querySelector('#CartDrawer');
        const freshDrawer = html.querySelector('cart-drawer');
        const currentContents = cartDrawer.querySelector('#CartDrawer');
        // The drawer opened while this request was in flight (e.g. Add to bag): it already shows the new cart.
        if (!freshContents || !freshDrawer || !currentContents || cartDrawer.classList.contains('active')) return;

        currentContents.innerHTML = freshContents.innerHTML;
        cartDrawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
        cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
      })
      .catch((error) => console.error(error));
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartDrawer);
  }

  /*
   * Drawer header count and checkout total (snippets/cart-drawer.liquid). Dawn's cart.js
   * (onCartUpdate) can replace only <cart-drawer-items> and .cart-drawer__footer, so the
   * header and the checkout button would keep old values: copy the current ones from the
   * data attributes rendered on those two elements.
   */
  function syncCartDrawerSummary(cartDrawer) {
    const items = cartDrawer.querySelector('cart-drawer-items[data-cart-count-text]');
    const footer = cartDrawer.querySelector('.cart-drawer__footer[data-cart-total]');
    const count = cartDrawer.querySelector('[data-cart-drawer-count]');
    const total = cartDrawer.querySelector('[data-cart-drawer-checkout-total]');

    if (items && count) {
      const text = `· ${items.dataset.cartCountText}`;
      if (count.textContent.trim() !== text) count.textContent = text;
    }
    if (footer && total) {
      const text = `· ${footer.dataset.cartTotal}`;
      if (total.textContent.trim() !== text) total.textContent = text;
    }
  }

  customElements.whenDefined('cart-drawer').then(() => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    new MutationObserver(() => syncCartDrawerSummary(cartDrawer)).observe(cartDrawer, {
      childList: true,
      subtree: true,
    });
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCartDrawer();
  });
})();
